import { getInvoiceByNo } from "./invoiceService";

/**
 * Los rechazos de Business Central que no se arreglan reintentando.
 *
 * `insertaFacturasVenta` rechaza con la cadena `error: <PREFIJO>: <texto>`, y el
 * terminal reintenta todo lo que no reconoce. Eso está bien para
 * `SERIE_NO_CONFIGURADA` o `SERIE_SIN_NUMERO`, que se arreglan en BC sin tocar la
 * caja, pero no para estos: reintentar manda lo mismo y recibe lo mismo, sin fin.
 *
 * La lista es explícita a propósito. Un prefijo que no está aquí se reintenta, y
 * BC no usa uno definitivo nuevo sin acordarlo antes en un contrato.
 *
 * Acuerdo `contrato-ubicacion-y-correo-fe-cri-2026-09-13.md`, fila T1.
 *
 * `ABONO_EXCEDE` entra con el acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`,
 * H2 · TPV: la factura ya tiene abonado ese producto, desde otra caja o en un
 * abono anterior, y reenviar la misma nota de crédito da el mismo rechazo. BC lo
 * manda en dos puertas: al importar las líneas y al registrar. `LINEA_REPETIDA`,
 * en cambio, no está: el terminal no puede producirlo, y si llegara se reintenta.
 *
 * `CLIENTE_BLOQUEADO` entra con la regla de Solme del 15/09/2026 (a un cliente
 * bloqueado no se le factura desde el terminal) y la extensión 1.0.4.122:
 * `insertaFacturasVenta` rechaza el documento antes de escribir nada si el
 * cliente, o el de facturación, no lo admite. Reintentar sin que alguien lo
 * desbloquee en BC da el mismo rechazo. Acuerdo
 * `contrato-sincronizacion-incremental-precios-2026-09-14.md`, Hallazgo 11.
 */
export const PREFIJOS_DEFINITIVOS = [
  "ABONO_SIN_FACTURA",
  "CORREO_FE_LARGO",
  "ABONO_EXCEDE",
  "CLIENTE_BLOQUEADO",
];

/**
 * Si Business Central rechazó una nota de crédito porque abona más de lo que
 * queda de la factura.
 *
 * Sin anclar, como `esRechazoPorPagoIncompleto`: en el registro el código llega
 * **dentro** del `error.message` del 400 de `Microsoft.NAV.post`, no al principio.
 * En la respuesta de las líneas llega con prefijo, y ahí lo lee
 * `esRechazoDefinitivo`.
 */
export const esRechazoPorAbonoQueExcede = (motivo) => /ABONO_EXCEDE/.test(String(motivo ?? ""));

/**
 * El motivo para la fila de una nota de crédito rechazada por `ABONO_EXCEDE`,
 * seguido de lo que dijo Business Central.
 *
 * @param {string} motivo Lo que dijo Business Central.
 */
export const avisoDelAbonoQueExcede = (motivo) => {
  const aviso =
    "Business Central no registró la nota de crédito: la factura ya tiene abonado ese producto, " +
    "desde otra caja o en un abono anterior. Trae Históricos y haz una nota de crédito por lo que queda.";
  const texto = String(motivo ?? "").trim();
  return texto ? `${aviso} ${texto}` : aviso;
};

/**
 * Si Business Central rechazó el documento porque el cliente está bloqueado.
 *
 * Anclado al prefijo: este rechazo llega siempre en la respuesta de la cabecera,
 * con `error: CLIENTE_BLOQUEADO: <texto de BC>`.
 */
export const esRechazoPorClienteBloqueado = (motivo) =>
  prefijoDelRechazo(motivo) === "CLIENTE_BLOQUEADO";

/**
 * El motivo para la fila de un documento rechazado por `CLIENTE_BLOQUEADO`,
 * seguido de lo que dijo Business Central sin el prefijo, que es para el
 * terminal y no para el cajero.
 *
 * @param {string} motivo Lo que dijo Business Central.
 */
export const avisoDelClienteBloqueado = (motivo) => {
  const aviso =
    "Business Central no registró el documento: el cliente está bloqueado. " +
    "Si lo desbloquean en Business Central, vuelve a sincronizarlo.";
  const texto = String(motivo ?? "")
    .replace(/^\s*(?:error:\s*)?CLIENTE_BLOQUEADO:\s*/, "")
    .trim();
  if (!texto) return aviso;
  // El texto de Microsoft llega sin punto final (medido en CRI DEV).
  return `${aviso} ${/[.!?]$/.test(texto) ? texto : `${texto}.`}`;
};

/**
 * El prefijo de un rechazo --`ABONO_SIN_FACTURA` de
 * `ABONO_SIN_FACTURA: el abono…`--, o null si el texto no trae uno.
 */
export const prefijoDelRechazo = (motivo) =>
  String(motivo ?? "").match(/^\s*(?:error:\s*)?([A-Z][A-Z0-9_]*):/)?.[1] ?? null;

const buscarLaFactura = async (numero) =>
  (await getInvoiceByNo(numero, "No")) || (await getInvoiceByNo(numero, "Pre_Assigned_No"));

/**
 * Si un rechazo de la cabecera es definitivo para este documento.
 *
 * `ABONO_SIN_FACTURA` cubre dos casos que BC no separa: el abono no dice qué
 * factura corrige, o esa factura todavía no está en BC. El segundo **se arregla
 * solo** en cuanto la factura sube, así que el abono sólo sale de la cola cuando
 * esperar no sirve de nada:
 *
 * - no dice qué factura corrige;
 * - la factura no está en el terminal;
 * - o ya tiene su número de BC y no le queda nada pendiente.
 *
 * Si la factura sigue sin número o pendiente, el abono se reintenta y sale
 * después de ella.
 *
 * @param {string} motivo    Lo que dijo Business Central.
 * @param {object} documento La cabecera local del documento rechazado.
 */
export const esRechazoDefinitivo = async (motivo, documento) => {
  const prefijo = prefijoDelRechazo(motivo);
  if (!PREFIJOS_DEFINITIVOS.includes(prefijo)) return false;
  if (prefijo !== "ABONO_SIN_FACTURA") return true;

  const numero = String(documento?.Applies_to_Doc_No ?? "").trim();
  if (!numero) return true;

  const factura = await buscarLaFactura(numero);
  if (!factura) return true;

  const pendiente = factura.pendingSync === 1 || factura.pendingSync === true;
  return Boolean(factura.No) && !pendiente;
};
